import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { PdfViewer, fileUrl } from "@/components/textbooks/PdfViewer";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { BookOpenText, Eye, Download, Search, GraduationCap, ClipboardCheck } from "lucide-react";

const groupBooks = (books) => {
  const tree = {};
  const sorted = [...books].sort((a, b) => (Number(a.grade_level) || 99) - (Number(b.grade_level) || 99));
  sorted.forEach((b) => {
    const cls = b.grade_level ? `Class ${b.grade_level}` : "Class group resources";
    ((tree[b.board] ??= {})[cls] ??= {})[b.subject] ??= [];
    tree[b.board][cls][b.subject].push(b);
  });
  return tree;
};

const ChapterCard = ({ b, onView }) => (
  <div className="qs-card p-4 flex items-center gap-4" data-testid={`library-book-${b.id}`}>
    <div className="w-12 h-14 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white grid place-items-center font-display font-bold shrink-0">
      {b.chapter_number ?? <BookOpenText className="w-5 h-5" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-medium text-slate-900 truncate">{b.chapter_title || b.title}</p>
      <p className="text-xs text-slate-500 truncate">{b.chapter_title ? b.title : b.description || `by ${b.uploaded_by_name}`}</p>
      {b.quizzes?.length > 0 && (
        <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700" data-testid={`library-quiz-badge-${b.id}`}>
          <ClipboardCheck className="w-3 h-3" /> {b.quizzes.length} quiz{b.quizzes.length > 1 ? "zes" : ""}
        </span>
      )}
    </div>
    <button onClick={() => onView(b)} className="btn-ghost text-xs" data-testid={`library-view-${b.id}`}><Eye className="w-4 h-4" /> Read</button>
    {b.allow_download && (
      <a href={fileUrl(b.id, true)} className="p-2 text-slate-500 hover:text-indigo-600" title="Download" data-testid={`library-download-${b.id}`}><Download className="w-4 h-4" /></a>
    )}
  </div>
);

const GradePicker = ({ grades, value, onPick }) => (
  <label className="flex items-center gap-2 text-sm text-slate-600">
    <GraduationCap className="w-4 h-4 text-indigo-500" /> My class
    <select value={value || ""} onChange={(e) => onPick(e.target.value)} className="bg-white border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" data-testid="library-grade-select">
      <option value="" disabled>Select…</option>
      {grades.map((g) => <option key={g} value={g}>Class {g}</option>)}
    </select>
  </label>
);

export default function Library() {
  const [books, setBooks] = useState([]);
  const [meta, setMeta] = useState({ boards: [], grades: [], my_grade: null });
  const [board, setBoard] = useState("All");
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/textbooks").then((r) => setBooks(r.data));
  useEffect(() => {
    load();
    api.get("/textbooks/meta").then((r) => setMeta(r.data));
  }, []);

  const openBook = (b) => {
    setViewing(b);
    api.post(`/textbooks/${b.id}/opened`).catch(() => {});
  };
  const openId = params.get("open");
  useEffect(() => {
    if (!openId || books.length === 0) return;
    const b = books.find((x) => x.id === openId);
    if (b) openBook(b);
    setParams({}, { replace: true });
  }, [openId, books]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickGrade = async (g) => {
    try {
      await api.patch("/textbooks/me/grade", { grade_level: g });
      setMeta((m) => ({ ...m, my_grade: g }));
      load();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const boards = ["All", ...new Set(books.map((b) => b.board))];
  const tree = useMemo(() => {
    const t = q.toLowerCase();
    return groupBooks(books.filter((b) => (board === "All" || b.board === board) &&
      [b.title, b.subject, b.chapter_title].some((s) => (s || "").toLowerCase().includes(t))));
  }, [books, board, q]);

  return (
    <AppShell title="Textbook library" subtitle="Read or download textbooks and chapters for your courses.">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, subject or chapter…" className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none" data-testid="library-search-input" />
        </div>
        <div className="flex gap-1.5" data-testid="library-board-filter">
          {boards.map((b) => (
            <button key={b} onClick={() => setBoard(b)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${board === b ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`library-board-${b}`}>{b}</button>
          ))}
        </div>
        <div className="ml-auto"><GradePicker grades={meta.grades} value={meta.my_grade} onPick={pickGrade} /></div>
      </div>

      {Object.keys(tree).length === 0 ? (
        <div className="qs-card p-12 text-center text-slate-500" data-testid="library-empty">
          <BookOpenText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          {meta.my_grade ? "No textbooks for your class yet." : "Select your class above to see your textbooks."}
        </div>
      ) : (
        <div className="space-y-10" data-testid="library-tree">
          {Object.entries(tree).map(([bd, classes]) => (
            <section key={bd}>
              <h2 className="font-display text-lg font-bold text-slate-900 mb-4">{bd}</h2>
              {Object.entries(classes).map(([cls, subjects]) => (
                <div key={cls} className="mb-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600 mb-3">{cls}</p>
                  {Object.entries(subjects).map(([sub, list]) => (
                    <div key={sub} className="mb-5">
                      <p className="text-sm font-semibold text-slate-700 mb-2">{sub} <span className="text-slate-400 font-normal">· {list.length}</span></p>
                      <div className="grid gap-3 md:grid-cols-2">{list.map((b) => <ChapterCard key={b.id} b={b} onView={openBook} />)}</div>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
      <PdfViewer book={viewing} onClose={() => setViewing(null)} canDownload={!!viewing?.allow_download} showQuizzes />
    </AppShell>
  );
}
